import React from 'react';
import {
  Box,
  Typography,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import { typo } from "../../../assets/fonts/font";
import colorConfigs from "../../../configs/colorConfigs";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useCart } from '../../../context/CartContext';
import { fetchOrderDateGroups } from '../../../api/OrderDateGroupAPI';
import { fetchOrderDateRules } from '../../../api/OrderDateRuleAPI';
import { OrderDateGroup } from '../../../models/IOrderDateGroup';
import { OrderDateRule } from '../../../models/IOrderDateRule';

interface CalendarProps {
  onDateTimeSelect: (date: string, time: string) => void;
}

const Calendar: React.FC<CalendarProps> = ({ onDateTimeSelect }) => {
  const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(null);
  const [selectedTime, setSelectedTime] = React.useState<string | null>(null);
  const [, setCurrentMonth] = React.useState<Dayjs>(dayjs());
  const { cart, setDeliveryDetails } = useCart();
  const [dateRules, setDateRules] = React.useState<OrderDateRule[]>([]);
  const [dateGroups, setDateGroups] = React.useState<OrderDateGroup[]>([]);
  const [loading, setLoading] = React.useState(true);

  const timeSlots = [
    { id: "morning", label: "9:00 AM - 1:00 PM" },
    { id: "afternoon", label: "1:00 PM - 6:00 PM" }
  ];

  React.useEffect(() => {
    const fetchRules = async () => {
      try {
        const [rules, groups] = await Promise.all([
          fetchOrderDateRules(),
          fetchOrderDateGroups()
        ]);
        setDateRules(rules);
        setDateGroups(groups);
      } catch (error) {
        console.error('Error fetching date rules:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, []);

  const cartSkus = React.useMemo(() => {
    return cart?.items?.map(item => parseInt(item.sku_sku)) || [];
  }, [cart]);

  const relevantGroups = React.useMemo(() => {
    return dateGroups.filter(group =>
      group.skus.some(sku => cartSkus.includes(sku))
    );
  }, [dateGroups, cartSkus]);

  const applicableRules = React.useMemo(() => {
    const groupIds = relevantGroups.map(group => group.id);
    return dateRules.filter(rule =>
      groupIds.includes(rule.order_date_group_id)
    );
  }, [dateRules, relevantGroups]);

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    if (selectedDate) {
      const timeRange = time === "morning" ? { from: "09:00", to: "13:00" } : { from: "13:00", to: "18:00" };

      const deliveryDetails = {
        online_order: true,
        delivery_date: selectedDate.format("YYYY-MM-DD"),
        delivery_time_from: timeRange.from,
        delivery_time_to: timeRange.to,
        self_collect: false,
        shipping_first_name: '',
        shipping_last_name: '',
        shipping_address: '',
        shipping_postal_code: '',
        shipping_phone_number: ''
      };

      setDeliveryDetails(deliveryDetails);
      onDateTimeSelect(selectedDate.format("MMMM D, YYYY"), time);
    }
  };

  const isDateDisabled = (date: Dayjs) => {
    if (applicableRules.length === 0) return false;

    const dayOfWeek = date.day();
    const month = date.month() + 1;
    const dayOfMonth = date.date();
    const today = dayjs().startOf("day");
    const daysFromToday = date.startOf("day").diff(today, 'day');

    const allowRules = applicableRules.filter(r => r.rule_effect === 'allow');
    const disAllowRules = applicableRules.filter(r => r.rule_effect === 'disallow');

    if (allowRules.length > 0) {
      let isAllowed = false;

      for (const rule of allowRules) {
        if (rule.month !== null && rule.month !== month) continue;

        switch (rule.type) {
          case 'before_days':
            isAllowed ||= daysFromToday < rule.values[0] + 1;
            break;
          case 'after_days':
            isAllowed ||= daysFromToday >= rule.values[0];
            break;
          case 'days_of_week':
            isAllowed ||= rule.values.includes(dayOfWeek);
            break;
          case 'date':
            isAllowed ||= rule.values.includes(dayOfMonth);
            break;
        }
      }

      return !isAllowed;
    }

    for (const rule of disAllowRules) {
      if (rule.month !== null && rule.month !== month) continue;

      switch (rule.type) {
        case 'before_days':
          if (daysFromToday < rule.values[0] + 1) return true;
          break;
        case 'after_days':
          if (daysFromToday >= rule.values[0]) return true;
          break;
        case 'days_of_week':
          if (rule.values.includes(dayOfWeek)) return true;
          break;
        case 'date':
          if (rule.values.includes(dayOfMonth)) return true;
          break;
      }
    }

    return false;
  };

  if (loading) {
    return (
      <Box sx={{
        border: `1px solid ${colorConfigs.tertiary}`,
        borderRadius: "8px",
        p: 2,
        mb: 2,
        backgroundColor: "var(--color-white)",
        textAlign: 'center'
      }}>
        <Typography sx={{ ...typo.bodyContent }}>Loading delivery options...</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{
        border: `1px solid ${colorConfigs.tertiary}`,
        borderRadius: "8px",
        p: 2,
        mb: 2,
        backgroundColor: "var(--color-white)",
      }}>
        <Typography sx={{ ...typo.bodyContent }}>Delivery Date</Typography>
        <DatePicker
          value={selectedDate}
          onChange={(newValue) => {
            if (newValue) {
              setSelectedDate(newValue);
              setCurrentMonth(newValue.startOf('month'));
              setSelectedTime(null);
            }
          }}
          shouldDisableDate={isDateDisabled}
          minDate={dayjs()}
          sx={{
            mb: 3,
            width: {
              xs: '100%',
              md: '30%'
            },
            '& .MuiInputBase-root': {
              border: '1px solid',
              borderColor: colorConfigs.primary,
              borderRadius: '4px',
              ...typo.bodyContent,
            },
            '& .MuiInputLabel-root': {
              ...typo.bodyContent,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
              boxShadow: 'none',
            }
          }}
          format="D MMMM, YYYY"
        />

        {selectedDate && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ ...typo.title, mb: 2 }}>Select Time Slot</Typography>
            <RadioGroup
              value={selectedTime || ""}
              onChange={(e) => handleTimeSelect(e.target.value)}
            >
              {timeSlots.map((slot) => (
                <FormControlLabel
                  key={slot.id}
                  value={slot.id}
                  control={
                    <Radio
                      sx={{
                        color: colorConfigs.primary,
                        '&.Mui-checked': {
                          color: colorConfigs.primary,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ ...typo.bodyContent }}>
                      {slot.label}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          </>
        )}

        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography sx={{ ...typo.title }}>Local Delivery</Typography>
          <Typography sx={{ ...typo.bodyContent }}>
            Please note that factors like traffic and location allocation may affect delivery schedules.
            We appreciate your understanding in unforeseen circumstances, and we will deliver your order
            within the stipulated time range for your order. Delivery slots are applicable for orders
            placed before 2.30pm. For orders affected, we will have to contact you separately to reschedule.
          </Typography>
        </Box>

        {selectedDate && selectedTime && (
          <Box sx={{
            mt: 2,
            p: 2,
            backgroundColor: colorConfigs.secondary,
            borderRadius: "4px"
          }}>
            <Typography sx={{ ...typo.bodyContent }}>
              <strong>Selected Delivery:</strong> {selectedDate.format("MMMM D, YYYY")}, {
                timeSlots.find(slot => slot.id === selectedTime)?.label
              }
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default Calendar;